/**
 * OTWhatsAppPanel.jsx · v101.267.0 · WhatsApp del cliente en la columna DERECHA del detalle de la OT.
 * Agendadores y supervisión atienden aquí mismo, sin ir a Conversaciones: Tomar, Devolver a Fibi, Cerrar, escribir al
 * cliente (emojis, stickers, saludo cuando pasó la ventana de 24 h) y PDF. Es el mismo componente <Chat> de
 * WhatsApp › Conversaciones (mismas reglas y endpoints /v1/fibi/conversaciones/{id}/*).
 * Los demás roles con acceso (fiscalizador, finanzas, soporte) la ven en solo lectura. Si no hay conversación, el agente
 * puede pedir a Fibi que contacte al cliente.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Bot, ExternalLink, Loader2, MessageCircle } from "lucide-react";
import { Chat } from "../pages/FibiConversaciones";
import OTWhatsAppChat from "./OTWhatsAppChat";

const ROLES_AGENTE = ["super_admin", "admin", "admin_zone", "supervisor", "agendador", "scheduler", "atc"];
const ESTADO = { en_espera: "En espera", humano: "Con agente", bot: "Con Fibi", cerrada: "Cerrada" };
const hora = (v) => (v ? new Date(v).toLocaleString("es-EC", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");
const OUT = "-6px -6px 14px rgba(255,255,255,.95), 6px 6px 14px rgba(163,177,198,.55)";
const caja = { borderRadius: 20, background: "#ECF0F5", padding: 14, boxShadow: OUT, display: "flex", flexDirection: "column", gap: 10 };

export default function OTWhatsAppPanel({ odooTaskId }) {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const agente = ROLES_AGENTE.includes(String(user?.role || "").toLowerCase());
  const [items, setItems] = useState(undefined);   // undefined = cargando · null = sin permiso
  const [sel, setSel] = useState(null);
  const [acceso, setAcceso] = useState({});         // {cid: true | false} · false = conversación de otra sucursal
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!odooTaskId) return;
    try {
      const { data } = await api.get(`/v1/fibi/ot/${odooTaskId}`);
      const its = (data.items || []).slice().sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
      setItems(its);
      // Por defecto: la abierta más reciente; si no hay abiertas, la última
      setSel((s) => (s && its.some((c) => c.id === s) ? s : (its.find((c) => c.estado !== "cerrada") || its[0])?.id ?? null));
    } catch (e) { setItems(e?.response?.status === 403 ? null : []); }
  }, [odooTaskId]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  // El agente solo puede operar conversaciones de su sucursal: se comprueba una vez antes de montar el chat
  useEffect(() => {
    if (!agente || !sel || acceso[sel] !== undefined) return;
    api.get(`/v1/fibi/conversaciones/${sel}`)
      .then(() => setAcceso((a) => ({ ...a, [sel]: true })))
      .catch(() => setAcceso((a) => ({ ...a, [sel]: false })));
  }, [agente, sel, acceso]);

  const contactar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/v1/fibi/iniciar", { odoo_task_id: odooTaskId }, { noProcess: true });
      toast.success("Fibi saludó al cliente por WhatsApp");
      setSel(data.conversacion); await load();
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "No se pudo contactar");
    } finally { setBusy(false); }
  };

  if (items === null) return null;
  // Solo lectura: roles sin atención al cliente, o conversación de otra sucursal
  if (!agente || (sel && acceso[sel] === false)) return <OTWhatsAppChat odooTaskId={odooTaskId} alto={640} />;

  return (
    <div data-testid="ot-whatsapp-panel" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ ...caja, padding: "10px 14px", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, color: "#0A3747", fontSize: 14 }}>
          <span style={{ width: 28, height: 28, borderRadius: 9, background: "#DCFCE7", color: "#16A34A",
                         display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <MessageCircle size={15} />
          </span>
          WhatsApp del cliente
        </span>
        <button onClick={() => navigate(sel ? `/whatsapp/conversaciones?c=${sel}` : "/whatsapp/conversaciones")}
          title="Abrir en la bandeja de Conversaciones"
          style={{ border: 0, borderRadius: 9, padding: 6, background: "#E4EAF0", cursor: "pointer", color: "#0A3747" }}>
          <ExternalLink size={14} />
        </button>
      </div>

      {items === undefined && <div style={{ ...caja, fontSize: 12, color: "#64748B" }}><Loader2 size={16} className="animate-spin" /> Cargando conversación…</div>}

      {items && items.length === 0 && (
        <div style={{ ...caja, fontSize: 12.5, color: "#475569" }}>
          Aún no hay conversación de WhatsApp con el cliente de esta OT.
          <div>
            <button onClick={contactar} disabled={busy} data-testid="ot-whatsapp-contactar"
              style={{ border: 0, borderRadius: 11, padding: "8px 12px", background: "#0A3747", color: "#FFF", cursor: "pointer",
                       fontSize: 12.5, fontWeight: 600, display: "inline-flex", gap: 6, alignItems: "center" }}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Bot size={14} />} Contactar con Fibi
            </button>
          </div>
        </div>
      )}

      {items && items.length > 1 && (
        <select value={sel ?? ""} onChange={(e) => setSel(Number(e.target.value))}
          style={{ width: "100%", fontSize: 12, borderRadius: 10, border: "1px solid #CBD5E1", padding: "6px 8px", background: "#FFF" }}>
          {items.map((c) => (
            <option key={c.id} value={c.id}>#{c.id} · {hora(c.created_at)} · {ESTADO[c.estado] || c.estado}</option>
          ))}
        </select>
      )}

      {sel && acceso[sel] === undefined && <div style={{ ...caja, fontSize: 12, color: "#64748B" }}><Loader2 size={16} className="animate-spin" /></div>}
      {sel && acceso[sel] === true && <Chat key={sel} cid={sel} onChanged={load} />}
    </div>
  );
}
